import { FC } from 'react';

import { Chip, Paper, TextField } from '@mui/material';

import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import MetadataTypeDefinition from '@/shared/types/metadata/metadataTypeDefinition.type';

import CreateMetadataFormFields from '../types/createMetadataFormFields.type';
import Module from '../types/module.type';
import getRequiredLevelOptions from '../utils/getRequiredLevelOptions';

type FormProps = {
  module: Module;
  formValues: CreateMetadataFormFields;
  formErrors: Record<string, string>;
  metadataTypes: MetadataTypeDefinition;
  getFormValue: (value: Record<string, unknown>) => void;
};

const Form: FC<FormProps> = ({
  module,
  formValues,
  formErrors,
  metadataTypes,
  getFormValue,
}) => {
  return (
    <Paper elevation={0} sx={{ py: 2 }}>
      <WaveUIForm_V2
        fields={[
          {
            columns: 6,
            field_type: FieldType.Text,
            field_props: {
              name: 'metadata_key',
              label: 'Metadata Key',
              required: true,
              placeholder: 'Enter metadata key',
              value: formValues.metadata_key,
              error: !!formErrors?.metadata_key,
              helperText: formErrors?.metadata_key,
            },
          },
          {
            columns: 6,
            field_type: FieldType.Select,
            field_props: {
              name: 'metatype_id',
              label: 'Metatype ID',
              required: true,
              placeholder: 'Select metatype ID',
              value: formValues.metatype_id,
              options: Object.keys(metadataTypes).map((key) => ({
                value: key,
                label: key,
              })),
              error: !!formErrors?.metatype_id,
              helperText: formErrors?.metatype_id,
            },
          },
          {
            field_type: FieldType.Autocomplete,
            field_props: {
              name: 'required_level',
              multiple: true,
              label: 'Required On',
              placeholder: 'Select required level',
              value: formValues.required_level,
              options: getRequiredLevelOptions({ module }),
              isOptionEqualToValue: (option, value) => {
                const { label: optLabel } = option as {
                  label: string;
                  value: string;
                };
                const { label: valLabel } = value as {
                  label: string;
                  value: string;
                };

                return optLabel === valLabel;
              },
              renderInput: (params) => <TextField {...params} />,
              renderTags(value, getTagProps) {
                return value.map((option, index) => {
                  const { key, ...other } = getTagProps({ index });
                  const { label } = option as { label: string; value: string };

                  return (
                    <Chip key={key} label={label} variant='filled' {...other} />
                  );
                });
              },
            },
          },
          {
            field_type: FieldType.Text,
            field_props: {
              name: 'metadata_category',
              label: 'Metadata Category',
              placeholder: 'Enter metadata category',
              value: formValues.metadata_category,
            },
          },
          {
            field_type: FieldType.Text,
            field_props: {
              name: 'description',
              label: 'Description',
              placeholder: 'Enter description',
              value: formValues.description,
              multiline: true,
              rows: 4,
            },
          },
        ]}
        getFieldsValues={getFormValue}
      />
    </Paper>
  );
};

export default Form;
